import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AuthUser } from "@app/auth/model/auth-user";
import { Customer } from "@app/customer/model/customer";
import { CustomerService } from "@app/customer/services/customer.service";
import { CustomerActions } from "@app/customer/store/actions";
import { Entity } from "@app/entity/model/entity";
import { EntityService } from "@app/entity/services/entity.service";
import { RouterGo } from "@app/root-store/actions";
import { emailValid, firstnameEmpty, humanNameValid, lastnameEmpty } from "@app/shared/validators";
import { AppErrorStateMatcher } from "@app/shared/validators/app-error-state-matcher.service";
import { UserRole } from "@app/user/model/user-role";
import { Observable } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap, tap } from "rxjs/operators";

@Component({
    selector: "app-customer-add",
    templateUrl: "./customer-add.component.html",
    styleUrls: ["./customer-add.component.scss"],
})
export class CustomerAddComponent implements OnInit {
    entities$: Observable<Entity[]>;
    userRoles$: Observable<UserRole[]>;
    customer$: Observable<Customer>;
    customerForm: FormGroup = this.formBuilder.group({
        firstName: ["", [firstnameEmpty, humanNameValid]],
        lastName: ["", [lastnameEmpty, humanNameValid]],
        email: ["", emailValid],
        phone: ["", Validators.compose([Validators.pattern("^[0-9]+$"), Validators.minLength(11)])],
        phone2: ["", Validators.compose([Validators.pattern("^[0-9]+$"), Validators.minLength(11)])],
        phone3: ["", Validators.compose([Validators.pattern("^[0-9]+$"), Validators.minLength(11)])],
        phoneType: [""],
        phone2Type: [""],
        phone3Type: [""],
        entityId: ["", Validators.required],
        roleId: [1, Validators.required],
        accountNumber: [""],
    });
    submitAttempted = false;

    phoneTypeList = ["Home", "Cell", "Work"];

    // Overridden by display-customer-info-component
    @Output()
    emitter = new EventEmitter<CustomerActions | RouterGo>();

    constructor(
        public errorMatcher: AppErrorStateMatcher,
        private readonly formBuilder: FormBuilder,
        private readonly customerService: CustomerService,
        private readonly entityService: EntityService
    ) {}

    ngOnInit() {
        this.userRoles$ = this.customerService.dispatchAndSelectUserRoles();
        this.entities$ = this.customerService.selectAuthUser().pipe(
            tap((user: AuthUser) => (user ? this.customerForm.patchValue({ entityId: user.entityId }) : null)),
            switchMap(() => this.entityService.selectEntitiesArray$())
        );

        this.customerService.subscribeToEmitter(this.emitter);

        this.customerForm.controls.email.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(value => {
            this.customerForm.controls.email.setValue(value?.toLowerCase());
        });
    }

    get fields() {
        return this.customerForm.controls;
    }

    stripPhoneFormat(phone: string) {
        return (phone || "").replace(/[()-. ]/g, "");
    }

    saveCustomer(customer?: Customer) {
        this.submitAttempted = true;

        const subscription = this.emitter.subscribe(() => {
            this.submitAttempted = false;
            subscription.unsubscribe();
        });

        this.customerForm.patchValue(
            {
                phone: this.stripPhoneFormat(this.customerForm.controls.phone.value),
                phone2: this.stripPhoneFormat(this.customerForm.controls.phone2.value),
                phone3: this.stripPhoneFormat(this.customerForm.controls.phone3.value),
            },
            { onlySelf: true, emitEvent: false }
        );

        this.customerService.upsertCustomer(this.customerForm, this.emitter, customer);
    }

    cancel() {
        this.emitter.emit(this.customerService.cancelAction());
    }
}
